import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/components/AuthProvider';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MerchantApplicationForm, ApplicationCompletionResult } from './components/MerchantApplicationForm';
import { db } from '@/lib/firebase';
import { doc, getDoc, setDoc, serverTimestamp, Timestamp, arrayUnion } from 'firebase/firestore';
import { CheckCircle, Clock, Shield, DollarSign, CreditCard, FileText, ShieldCheck, Loader2, RefreshCw, AlertCircle, Eye, Download } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { getCompany } from '@/lib/api';
import { ApplicationSource } from './types/fieldSettings';
import type { IntegrationLogEntry } from './types';
import { payarcService } from '@/services/payarcService';
import { useToast } from '@/components/ui/use-toast';
import { applicationSourceService } from '@/features/application-sources/services/applicationSourceService';

// Import debug utilities for development
import './utils/clearSettings';
import './utils/debugFieldVisibility';

const MerchantApplication = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [applicationStatus, setApplicationStatus] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentApplicationId, setCurrentApplicationId] = useState<string | null>(null);
  const [payArcMerchantCode, setPayArcMerchantCode] = useState<string | null>(null);
  const [termsDisplayUrl, setTermsDisplayUrl] = useState<string | null>(null);
  const [termsDownloadUrl, setTermsDownloadUrl] = useState<string | null>(null);
  const [termsLoading, setTermsLoading] = useState(false);
  const [termsError, setTermsError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [isSubmittingSignature, setIsSubmittingSignature] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitErrorDetails, setSubmitErrorDetails] = useState<string | null>(null);
  const [isSubmitErrorDialogOpen, setIsSubmitErrorDialogOpen] = useState(false);
  const [termsDocSaved, setTermsDocSaved] = useState(false);
  const [storedTermsUrl, setStoredTermsUrl] = useState<string | null>(null);

  // Load application source from URL params or use first active source as fallback
  const [applicationSource, setApplicationSource] = useState<ApplicationSource | null>(null);

  useEffect(() => {
    const loadApplicationSource = async () => {
      const sourceParam = searchParams.get('source') as ApplicationSource;
      if (sourceParam) {
        setApplicationSource(sourceParam);
      } else {
        // Fallback to first active source from collection
        try {
          const sources = await applicationSourceService.getActiveApplicationSources();
          if (sources.length > 0) {
            setApplicationSource(sources[0].id as ApplicationSource);
          }
        } catch (error) {
          console.error('Failed to load application sources:', error);
        }
      }
    };
    loadApplicationSource();
  }, [searchParams]);

  const hostedTermsUrl = (() => {
    const candidate = termsDownloadUrl || storedTermsUrl;
    if (candidate && /^https?:/i.test(candidate)) {
      return candidate;
    }
    return null;
  })();

  const { data: company } = useQuery({
    queryKey: ['company', user?.companyId],
    queryFn: () => getCompany(user?.companyId as string),
    enabled: !!user?.companyId
  });

  const agreementApiUrl = payArcMerchantCode
    ? `/api/payarc/applications/${payArcMerchantCode}/terms-and-conditions`
    : null;

  const recordIntegrationLog = useCallback(
    async (entry: Omit<IntegrationLogEntry, 'timestamp'> & { timestamp?: string }) => {
      if (!currentApplicationId) return;

      const logEntry: IntegrationLogEntry = {
        ...entry,
        timestamp: entry.timestamp ?? new Date().toISOString(),
      };

      try {
        await setDoc(
          doc(db, 'merchantApplications', currentApplicationId),
          {
            'data.integrationLogs': arrayUnion(logEntry),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      } catch (error) {
        console.error('Unable to record integration log:', error);
      }
    },
    [currentApplicationId]
  );

  useEffect(() => {
    const checkApplicationStatus = async () => {
      if (!company) return;

      try {
        const companyDoc = await getDoc(doc(db, 'companies', company.id));
        const companyData = companyDoc.data();
        
        if (companyData?.merchantApplication?.status) {
          setApplicationStatus(companyData.merchantApplication.status);
        }

        if (companyData?.merchantApplication?.applicationId) {
          setCurrentApplicationId(companyData.merchantApplication.applicationId);
        } else {
          setCurrentApplicationId(`${company.id}_application`);
        }

        if (companyData?.merchantApplication?.payArcMerchantCode) {
          setPayArcMerchantCode(companyData.merchantApplication.payArcMerchantCode);
        }
      } catch (error) {
        console.error('Error checking application status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkApplicationStatus();
  }, [company]);

  const handleApplicationComplete = (result?: ApplicationCompletionResult) => {
    if (result?.applicationId) {
      setCurrentApplicationId(result.applicationId);
    }

    if (result?.merchantCode) {
      setPayArcMerchantCode(result.merchantCode);
    }

    if (result?.status === 'awaiting_signature') {
      setTermsDisplayUrl(null);
      setTermsDownloadUrl(null);
      setTermsError(null);
      setStoredTermsUrl(null);
      setTermsDocSaved(false);
      setTermsLoading(false);
    }

    setSubmitError(null);

    setApplicationStatus(result?.status ?? 'submitted');
    setShowForm(false);
  };

  useEffect(() => {
    if (!currentApplicationId) return;

    const fetchApplicationMetadata = async () => {
      try {
        const applicationSnap = await getDoc(doc(db, 'merchantApplications', currentApplicationId));
        if (applicationSnap.exists()) {
          const applicationData = applicationSnap.data();
          const storedCode =
            applicationData?.data?.payArcMerchantCode ||
            applicationData?.payArcMerchantCode ||
            null;

          if (storedCode) {
            setPayArcMerchantCode(storedCode);
          }

          const extractTermsDoc = (value: any): any => {
            if (!value) return null;
            if (Array.isArray(value)) {
              return value.length > 0 ? value[value.length - 1] : null;
            }
            if (typeof value === 'object') {
              return value;
            }
            return null;
          };

          const documentsRoot = applicationData?.documents || {};
          const documentsNested = applicationData?.data?.documents || {};
          const legacyDoc = applicationData?.payarc_terms || applicationData?.data?.payarc_terms;

          const termsDocCandidate =
            extractTermsDoc(documentsRoot?.payarc_terms) ||
            extractTermsDoc(documentsNested?.payarc_terms) ||
            extractTermsDoc(legacyDoc);

          const termsDocUrl = termsDocCandidate?.url || termsDocCandidate?.downloadUrl;

          if (termsDocUrl) {
            setStoredTermsUrl(termsDocUrl);
            setTermsDownloadUrl((prev) => prev || termsDocUrl);
            setTermsDisplayUrl((prev) => prev || termsDocUrl);
            setTermsLoading(false);
            setTermsDocSaved(true);
          }
        }
      } catch (error) {
        console.error('Error loading application metadata:', error);
      }
    };

    fetchApplicationMetadata();
  }, [currentApplicationId]);

  useEffect(() => {
    if (applicationStatus !== 'awaiting_signature') return;
    if (!payArcMerchantCode) return;
    if (!currentApplicationId) return;

    if (storedTermsUrl && !termsDisplayUrl) {
      setTermsDisplayUrl(storedTermsUrl);
      setTermsDownloadUrl((prev) => prev || storedTermsUrl);
      setTermsDocSaved(true);
    }

    if (termsDisplayUrl || termsLoading || termsError) return;

    let cancelled = false;

    const loadTerms = async () => {
      try {
        setTermsLoading(true);
        const pdfBlob = await payarcService.fetchTermsAndConditions(payArcMerchantCode);
        if (cancelled) return;

        const objectUrl = URL.createObjectURL(pdfBlob);
        setTermsDisplayUrl(objectUrl);
        setTermsDownloadUrl((prev) => prev || agreementApiUrl || objectUrl);
        setTermsError(null);

        if (!termsDocSaved && agreementApiUrl) {
          const storedAt = Timestamp.now();
          const documentRecord = {
            name: `PayArc Terms - ${payArcMerchantCode}.pdf`,
            url: agreementApiUrl,
            merchantCode: payArcMerchantCode,
            uploadedAt: storedAt,
            source: 'payarc',
          };

          await setDoc(
            doc(db, 'merchantApplications', currentApplicationId!),
            {
              'documents.payarc_terms': documentRecord,
              'data.documents.payarc_terms': documentRecord,
              'documentsMetadata.payarc_terms.savedAt': serverTimestamp(),
            },
            { merge: true }
          );
          setStoredTermsUrl(agreementApiUrl);
          setTermsDocSaved(true);
        }
      } catch (error: any) {
        if (cancelled) return;
        console.error('Failed to fetch PayArc terms:', error);
        const message = error?.message || 'Unable to fetch terms and conditions right now.';
        setTermsError(message);
        toast({
          title: 'Terms unavailable',
          description: 'We could not download the agreement automatically. Use the retry option below.',
          variant: 'destructive',
        });
      } finally {
        if (!cancelled) {
          setTermsLoading(false);
        }
      }
    };

    loadTerms();

    return () => {
      cancelled = true;
    };
  }, [
    applicationStatus,
    payArcMerchantCode,
    termsDisplayUrl,
    termsLoading,
    termsError,
    storedTermsUrl,
    termsDocSaved,
    agreementApiUrl,
    currentApplicationId,
    toast,
  ]);

  useEffect(() => () => {
    if (termsDisplayUrl && termsDisplayUrl.startsWith('blob:')) {
      URL.revokeObjectURL(termsDisplayUrl);
    }
  }, [termsDisplayUrl]);

  const submitForUnderwriting = useCallback(
    async ({ silent = false } = {}) => {
      if (!payArcMerchantCode || !currentApplicationId || !company?.id) {
        throw new Error('Missing application context');
      }

      try {
        // Get the assigned banking partner from the application
        const applicationSnap = await getDoc(doc(db, 'merchantApplications', currentApplicationId));
        const assignedBanks = applicationSnap.exists() ? applicationSnap.data()?.assignedBanks : [];
        const payArcBankId = assignedBanks && assignedBanks.length > 0 ? assignedBanks[0] : undefined;

        const response = await payarcService.submitApplicationForUnderwriting(
          payArcMerchantCode,
          payArcBankId
        );

        if (!response.success) {
          const message = response.message || 'Failed to submit PayArc application';
          if (!silent) {
            toast({
              title: 'Submission failed',
              description: message,
              variant: 'destructive',
            });
          }
          await recordIntegrationLog({
            source: 'payarc-submit',
            status: 'error',
            message,
            actor: 'application',
            details: JSON.stringify(response.errors ?? response, null, 2),
          });
          const error = new Error(message);
          (error as any).details = response.errors ?? response;
          throw error;
        }

        const submissionTimestamp = new Date();

        try {
          await setDoc(
            doc(db, 'merchantApplications', currentApplicationId),
            {
              status: 'submitted',
              pipelineStatus: 'underwriting',
              underwritingSubmission: {
                submittedAt: submissionTimestamp,
                applicationId: currentApplicationId,
                payArcMerchantCode,
              },
            },
            { merge: true }
          );
        } catch (persistenceError) {
          await recordIntegrationLog({
            source: 'payarc-submit',
            status: 'error',
            message: 'Failed to persist PayArc submission metadata',
            actor: 'application',
            details: persistenceError instanceof Error
              ? persistenceError.stack || persistenceError.message
              : JSON.stringify(persistenceError, null, 2),
          });
          if (persistenceError instanceof Error) {
            (persistenceError as any).details = persistenceError.stack || persistenceError.message;
          }
          throw persistenceError;
        }

        await recordIntegrationLog({
          source: 'payarc-submit',
          status: 'success',
          message: 'Submitted PayArc application to underwriting',
          actor: 'application',
          details: response?.data ? JSON.stringify(response.data, null, 2) : undefined,
        });

        if (!silent) {
          toast({
            title: 'Agreement submitted',
            description: 'Your PayArc application has been sent to underwriting.',
          });
        }

        setSubmitError(null);
        setSubmitErrorDetails(null);
        setIsSubmitErrorDialogOpen(false);
        setApplicationStatus('submitted');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to submit PayArc application';
        if (!silent) {
          toast({
            title: 'Submission failed',
            description: message,
            variant: 'destructive',
          });
        }
        if (!silent) {
          const details = error instanceof Error
            ? ((error as any).details
                ? typeof (error as any).details === 'string'
                  ? (error as any).details
                  : JSON.stringify((error as any).details, null, 2)
                : error.stack || error.message)
            : undefined;
          setSubmitError(message);
          setSubmitErrorDetails(details || null);
          setIsSubmitErrorDialogOpen(Boolean(details));
        }
        throw error;
      }
    },
    [payArcMerchantCode, currentApplicationId, company?.id, toast, recordIntegrationLog]
  );

  const handleDownloadTerms = () => {
    const href = termsDownloadUrl || termsDisplayUrl || storedTermsUrl || agreementApiUrl;
    if (!href) return;

    const link = document.createElement('a');
    link.href = href;
    link.target = '_blank';
    link.rel = 'noopener';
    link.download = `payarc-terms-${payArcMerchantCode || 'agreement'}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleRetryFetchTerms = () => {
    setTermsError(null);
    setTermsDisplayUrl(null);
    setTermsDownloadUrl(null);
    setStoredTermsUrl(null);
    setTermsDocSaved(false);
    setTermsLoading(false);
    setSubmitError(null);
  };

  const handleOpenPreview = async () => {
    if (previewLoading) return;
    if (termsDisplayUrl) {
      setPreviewOpen(true);
      return;
    }

    const href = storedTermsUrl || termsDownloadUrl || agreementApiUrl || termsDisplayUrl;
    if (!href) return;

    try {
      setPreviewLoading(true);
      const response = await fetch(href, { credentials: 'include' });
      if (!response.ok) {
        throw new Error('Failed to fetch agreement preview');
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      setTermsDisplayUrl(objectUrl);
      setPreviewOpen(true);
    } catch (error) {
      console.error('Preview load error:', error);
      toast({
        title: 'Preview unavailable',
        description: 'We could not load the PDF preview. Try downloading instead.',
        variant: 'destructive',
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmitForUnderwriting = async () => {
    try {
      setIsSubmittingSignature(true);
      setSubmitError(null);
      setSubmitErrorDetails(null);
      await submitForUnderwriting();
    } catch (error) {
      console.error('Failed to submit PayArc application for underwriting:', error);
      setSubmitError(error instanceof Error ? error.message : 'Unable to submit application.');
    } finally {
      setIsSubmittingSignature(false);
    }
  };

  const renderAwaitingSignature = () => {
    const previewSource = termsDisplayUrl || storedTermsUrl || termsDownloadUrl || agreementApiUrl;
    const downloadHref = termsDownloadUrl || storedTermsUrl || agreementApiUrl || termsDisplayUrl;
    const viewHref = termsDisplayUrl || hostedTermsUrl;
    const fetchingAgreement = termsLoading && !downloadHref;

    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Card>
          <CardContent className="py-12 space-y-8">
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <FileText className="h-8 w-8 text-blue-600" />
                </div>
              </div>
              <h2 className="text-2xl font-bold">Review PayArc Terms & Conditions</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Please review the PayArc agreement and submit it for underwriting when you are ready.
              </p>
              {payArcMerchantCode && (
                <p className="text-sm text-gray-500">
                  Merchant Code:{' '}
                  <span className="font-mono text-gray-700">{payArcMerchantCode}</span>
                </p>
              )}
            </div>

            {submitError && (
              <Alert variant="destructive" className="flex flex-col gap-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-1" />
                  <AlertDescription className="text-sm text-red-700">
                    {submitError}
                  </AlertDescription>
                </div>
                <div className="flex flex-wrap gap-2">
                  {submitErrorDetails && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-200 text-red-700 hover:text-red-800 hover:border-red-300"
                      onClick={() => setIsSubmitErrorDialogOpen(true)}
                    >
                      View details
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-700 hover:text-red-800"
                    onClick={() => {
                      setSubmitError(null);
                      setSubmitErrorDetails(null);
                      setIsSubmitErrorDialogOpen(false);
                    }}
                  >
                    Clear
                  </Button>
                </div>
              </Alert>
            )}

            {fetchingAgreement && (
              <div className="flex flex-col items-center gap-3 py-6">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                <p className="text-sm text-gray-600">Fetching agreement from PayArc…</p>
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-3">
              <Button
                variant="outline"
                className="w-full flex items-center justify-center gap-2"
                onClick={handleOpenPreview}
                disabled={!previewSource || previewLoading}
              >
                {previewLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Opening…
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4" />
                    View Terms
                  </>
                )}
              </Button>

              <Button
                variant="outline"
                className="w-full flex items-center justify-center gap-2"
                onClick={handleDownloadTerms}
                disabled={!downloadHref}
              >
                <Download className="h-4 w-4" />
                Download PDF
              </Button>

              <Button
                className="w-full flex items-center justify-center gap-2"
                onClick={handleSubmitForUnderwriting}
                disabled={!downloadHref || isSubmittingSignature}
              >
                {isSubmittingSignature ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Submitting…
                  </>
                ) : (
                  'Agree & Submit'
                )}
              </Button>
            </div>

            {(termsDocSaved || hostedTermsUrl) && (
              <p className="text-xs text-center text-blue-700/80">
                A copy of the agreement has been stored in the application documents.
              </p>
            )}
          </CardContent>
        </Card>

        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="flex h-[90vh] w-full max-w-5xl flex-col gap-0 overflow-hidden p-0 sm:max-w-[90vw]">
            <div className="flex items-center gap-2 border-b px-6 py-4">
              <FileText className="h-5 w-5 text-blue-600" />
              <DialogTitle className="text-left text-lg font-semibold">PayArc Terms & Conditions</DialogTitle>
            </div>
            <div className="flex-1 min-h-0 bg-slate-100">
              {previewLoading ? (
                <div className="flex h-full items-center justify-center text-sm text-gray-500">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                </div>
              ) : viewHref ? (
                <iframe
                  src={viewHref}
                  title="PayArc Terms"
                  className="h-full w-full"
                  allowFullScreen
                />
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-gray-500">
                  Agreement not available yet.
                </div>
              )}
            </div>
          </DialogContent>
      </Dialog>

      <Dialog open={isSubmitErrorDialogOpen} onOpenChange={(open) => {
        setIsSubmitErrorDialogOpen(open);
        if (!open && !submitError) {
          setSubmitErrorDetails(null);
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogTitle>Error Details</DialogTitle>
          <div className="mt-4 max-h-[50vh] overflow-auto rounded border border-slate-200 bg-slate-50 p-4 text-sm font-mono whitespace-pre-wrap">
            {submitErrorDetails || 'No additional details provided.'}
          </div>
        </DialogContent>
      </Dialog>
      </div>
    );
  };

  const renderSubmittedCard = ({ showRetry, message }: { showRetry?: boolean; message?: string } = {}) => {
    const downloadHref = termsDownloadUrl || termsDisplayUrl || storedTermsUrl || agreementApiUrl;

    return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <Card>
        <CardContent className="py-12 space-y-6">
          <div className="text-center space-y-4">
            <div className="h-20 w-20 bg-green-100 rounded-full mx-auto flex items-center justify-center">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">The Hard Part Is Done!</h2>
            <p className="text-gray-600 max-w-md mx-auto">
              {message || "Welcome aboard! We've received your application and our team is reviewing all the details to get your account live as quickly as possible."}
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 max-w-md mx-auto">
            <h3 className="font-semibold text-blue-900 mb-2">What happens next?</h3>
            <ul className="text-sm text-blue-800 space-y-2">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <span>Our team will verify your information</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <span>We'll email you with any updates</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <span>Once approved, your account will be ready to go!</span>
              </li>
            </ul>
          </div>

          <div className="flex flex-col items-center gap-3 pt-2">
            <Button
              onClick={handleDownloadTerms}
              className="w-full md:w-auto"
              disabled={!downloadHref}
            >
              Download Agreement (PDF)
            </Button>
            <p className="text-sm text-gray-500 text-center max-w-sm">
              Keep this for your records. We'll be in touch soon!
            </p>
            {showRetry && (
              <Button variant="outline" onClick={handleRetryFetchTerms} className="gap-2">
                <RefreshCw className="h-4 w-4" />
                Try fetching the agreement again
              </Button>
            )}
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 max-w-md mx-auto">
            <p className="text-xs text-amber-700 text-center">
              <strong>Note:</strong> Once your account is approved and goes live, any test data currently on the account will be cleared to give you a fresh start.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (applicationStatus === 'approved') {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center space-y-4">
              <CheckCircle className="h-16 w-16 text-green-600 mx-auto" />
              <h2 className="text-2xl font-bold">You Can Now Accept Credit Cards!</h2>
              <p className="text-gray-600">
                Great news! You're all set to accept credit cards.
              </p>
              <button
                onClick={() => navigate('/app/settings')}
                className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
              >
                Go to Settings
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (applicationStatus === 'awaiting_signature') {
    if (termsError && !termsDisplayUrl && !termsDownloadUrl && !storedTermsUrl && !agreementApiUrl) {
      return renderSubmittedCard({
        showRetry: true,
        message: 'We are still generating your PayArc agreement. Please try again shortly.',
      });
    }

    return renderAwaitingSignature();
  }

  if (applicationStatus === 'submitted') {
    return renderSubmittedCard();
  }

  if (showForm && applicationSource) {
    return (
      <div className="container mx-auto px-4 py-8">
        <MerchantApplicationForm
          onComplete={handleApplicationComplete}
          applicationSource={applicationSource}
        />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Start Accepting Credit Cards Today
        </h1>
        <p className="text-xl text-gray-600">
          Join thousands of businesses who trust us with their payments
        </p>
      </div>

      <Card className="shadow-xl">
        <CardContent className="p-8">
          {/* Credit Card Logos */}
          <div className="text-center mb-8">
            <p className="text-sm text-gray-600 mb-4">Accept all major credit cards</p>
            <div className="flex justify-center items-center gap-4">
              {/* Visa */}
              <div className="bg-white p-2 rounded border border-gray-200 shadow-sm">
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg" 
                  alt="Visa" 
                  className="h-8 w-auto"
                />
              </div>
              {/* Mastercard */}
              <div className="bg-white p-2 rounded border border-gray-200 shadow-sm">
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" 
                  alt="Mastercard" 
                  className="h-8 w-auto"
                />
              </div>
              {/* American Express */}
              <div className="bg-white p-2 rounded border border-gray-200 shadow-sm">
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/f/fa/American_Express_logo_%282018%29.svg" 
                  alt="American Express" 
                  className="h-8 w-auto"
                />
              </div>
              {/* Discover */}
              <div className="bg-white p-2 rounded border border-gray-200 shadow-sm">
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/5/57/Discover_Card_logo.svg" 
                  alt="Discover" 
                  className="h-8 w-auto"
                />
              </div>
            </div>
          </div>

          {/* Benefits Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                <Clock className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Get Paid Fast</h3>
              <p className="text-gray-600">Receive funds in 24-48 hours directly to your bank account</p>
            </div>

            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                <DollarSign className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Keep 100% of Your Money</h3>
              <p className="text-gray-600">Receive 100% of your funds when using our platform fee feature</p>
            </div>

            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                <Shield className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Secure & Reliable</h3>
              <p className="text-gray-600">Bank-level security with PCI compliance and fraud protection</p>
            </div>
          </div>

          {/* Additional Benefits */}
          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="font-medium">No Setup Fees</p>
                  <p className="text-sm text-gray-600">Get started without any upfront costs</p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="font-medium">Transparent Pricing</p>
                  <p className="text-sm text-gray-600">Simple, competitive rates with no hidden fees</p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="font-medium">24/7 Support</p>
                  <p className="text-sm text-gray-600">Our team is here to help whenever you need us</p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="font-medium">Easy Integration</p>
                  <p className="text-sm text-gray-600">Start accepting payments in minutes, not days</p>
                </div>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center">
            <p className="text-gray-600 mb-4">
              Getting started takes just 5 minutes. No commitments, cancel anytime.
            </p>
            <button 
              className="bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 text-lg font-semibold shadow-lg transform transition hover:scale-105"
              onClick={() => setShowForm(true)}
            >
              Get Started Now - It's Free
            </button>
            <p className="text-xs text-gray-500 mt-3">
              No credit card required • No setup fees • Cancel anytime
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Trust Indicators */}
      <div className="mt-8 text-center">
        <div className="flex justify-center items-center gap-8 text-gray-500">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            <span className="text-sm">PCI Compliant</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            <span className="text-sm">SSL Secured</span>
          </div>
          <div className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            <span className="text-sm">Trusted by 10,000+ businesses</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MerchantApplication;
